/**
 * Tauri Integration Utilities
 * Handles platform detection and offline mode for desktop app
 */

// Check if running in Tauri desktop app
export const isTauri = () => {
  return typeof window !== 'undefined' && window.__TAURI__ !== undefined;
};

// Check if online (works in both web and Tauri)
export const isOnline = () => {
  return navigator.onLine;
};

// Get the appropriate API URL based on platform
export const getApiUrl = () => {
  // In Tauri, we still connect to the online backend for leaderboards
  // But we could add a local server option later
  return process.env.REACT_APP_BACKEND_URL || 'https://deckmaster-9.preview.emergentagent.com';
};

// Local storage keys for offline caching
const STORAGE_KEYS = {
  CACHED_SCORES: 'whichinghour_cached_scores',
  PENDING_SCORES: 'whichinghour_pending_scores',
  USER_DATA: 'whichinghour_user_data',
  LAST_SYNC: 'whichinghour_last_sync',
};

// Cache score locally (for offline play)
export const cacheScore = (scoreData) => {
  try {
    const pending = JSON.parse(localStorage.getItem(STORAGE_KEYS.PENDING_SCORES) || '[]');
    pending.push({
      ...scoreData,
      timestamp: new Date().toISOString(),
      synced: false,
    });
    localStorage.setItem(STORAGE_KEYS.PENDING_SCORES, JSON.stringify(pending));
    return true;
  } catch (e) {
    console.error('Failed to cache score:', e);
    return false;
  }
};

// Get pending scores that need to be synced
export const getPendingScores = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.PENDING_SCORES) || '[]');
  } catch (e) {
    return [];
  }
};

// Clear pending scores after successful sync
export const clearPendingScores = () => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_SCORES);
};

// Cache user data locally
export const cacheUserData = (userData) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  } catch (e) {
    console.error('Failed to cache user data:', e);
  }
};

// Get cached user data
export const getCachedUserData = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_DATA) || 'null');
  } catch (e) {
    return null;
  }
};

// Sync pending scores when back online
export const syncPendingScores = async (submitScoreFunc) => {
  if (!isOnline()) return { synced: 0, failed: 0 };
  
  const pending = getPendingScores();
  if (pending.length === 0) return { synced: 0, failed: 0 };
  
  let synced = 0;
  let failed = 0;
  
  for (const score of pending) {
    try {
      await submitScoreFunc(score);
      synced++;
    } catch (e) {
      failed++;
    }
  }
  
  if (synced > 0) {
    // Remove successfully synced scores
    const remaining = pending.slice(synced);
    if (remaining.length > 0) {
      localStorage.setItem(STORAGE_KEYS.PENDING_SCORES, JSON.stringify(remaining));
    } else {
      clearPendingScores();
    }
  }
  
  localStorage.setItem(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
  
  return { synced, failed };
};

// Listen for online/offline events
export const setupConnectivityListener = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// Platform info for analytics/debugging
export const getPlatformInfo = () => {
  return {
    isTauri: isTauri(),
    isOnline: isOnline(),
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };
};
